import { useEffect } from "react";
import { z } from "zod";
import { EventEmitter } from "../../EventEmitter";

// How often to check whether a newer build has been deployed. This is also the
// nag cadence: once a new version is out, a dismissed prompt reappears at the
// next check until the agent reloads.
const VERSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;

// The version this bundle was built as, inlined at build time by Vite.
const CURRENT_VERSION = import.meta.env.ALBERTINE_VERSION;

const versionResponseSchema = z.object({ version: z.string() });

async function fetchDeployedVersion(): Promise<string | undefined> {
    try {
        // Bypass the HTTP cache so we always see the version currently served,
        // not a stale copy from a previous check.
        const response = await fetch("/version.json", { cache: "no-store" });
        if (!response.ok) {
            return undefined;
        }
        const parsed = versionResponseSchema.safeParse(await response.json());
        return parsed.success ? parsed.data.version : undefined;
    } catch {
        // A failed check (offline, transient network error, invalid body) is not
        // actionable; the next check will try again.
        return undefined;
    }
}

// Polls the deployed version.json and prompts the agent to reload whenever a
// build newer than the one currently running is detected. The prompt keeps
// reappearing on each check until the agent reloads, even if they dismiss it.
export function useVersionCheck() {
    useEffect(() => {
        // Local dev has no deployed counterpart to compare against.
        if (import.meta.env.ALBERTINE_ENV === "localhost") {
            return;
        }

        let cancelled = false;

        const checkForUpdate = async () => {
            const deployedVersion = await fetchDeployedVersion();
            if (cancelled || !deployedVersion || deployedVersion === CURRENT_VERSION) {
                return;
            }
            // The toast layer keeps at most one update prompt on screen, so
            // re-emitting on every check simply re-nags after a dismissal
            // rather than stacking duplicates.
            EventEmitter.emit({
                name: "showToastMessage",
                params: { type: "update" },
            });
        };

        const interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL_MS);

        // Also check when the tab regains focus, so an agent returning to a
        // backgrounded tab is prompted promptly instead of waiting out the
        // interval.
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void checkForUpdate();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        void checkForUpdate();

        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);
}
